import type { Edge } from '@xyflow/react'
import { logError } from 'lib/logging'
import { upgradeStoredNodes } from './migrate'
import type { WorkflowNode, WorkflowSettings } from 'app/types/workflows'

// Local persistence so the build → save → reopen loop actually round-trips. Stands in for the real
// `workflows` GraphQL CRUD (spec §5.10); the doc shape is the same data the API would store.
// Scoped per user so drafts don't leak across accounts on a shared browser (cf. lib/threadNames).
const KEY_PREFIX = `trk-workflows`

function storageKey(userId: string | undefined): string {
  return `${KEY_PREFIX}:${userId ?? `anon`}`
}

export type WorkflowDoc = {
  id: string
  name: string
  active: boolean
  /** Set once the workflow has been saved live, and never cleared, so turning it off reads as Paused
   *  rather than Draft. The engine has no such state; it rides in `builderJson`. */
  wentLive?: boolean
  /** A short trigger summary for the list (derived from the trigger node at save time). */
  trigger: string
  triggerStartDate: string
  /** Runs the workflow for past events since `triggerStartDate` when it goes live. Absent reads as on. */
  backfill?: boolean
  /** The location this workflow runs for. Optional on the local draft type because drafts predate
   *  the field; the serializer blocks activation until one is chosen. */
  locationId?: string
  /** The workspace the draft was saved in. Storage is keyed per user, so without this a draft follows
   *  its author into every workspace they switch to. Absent on drafts saved before it was recorded. */
  workspaceId?: string
  nodes: WorkflowNode[]
  edges: Edge[]
  settings: WorkflowSettings
  updatedAt: number
  /** First save in this browser, stamped by `saveWorkflow`. Absent on drafts that predate it. */
  createdAt?: number
  /** The backend workflow id once this has been synced via createWorkflow. Absent for local-only
   *  drafts (e.g. a workflow that uses a not-yet-supported node and can't be created yet). */
  remoteId?: string
  /** A save wrote this copy but the server never accepted it, so it is newer than the server's.
   *  Only then does a synced workflow reopen from here instead of from the server. */
  unsynced?: boolean
}

function readAll(userId: string | undefined): Record<string, WorkflowDoc> {
  if (typeof window === `undefined`) return {}
  try {
    return JSON.parse(window.localStorage.getItem(storageKey(userId)) ?? `{}`) as Record<string, WorkflowDoc>
  } catch (error) {
    logError(`Failed to read saved workflows`, error)
    return {}
  }
}

function writeAll(userId: string | undefined, all: Record<string, WorkflowDoc>): void {
  if (typeof window === `undefined`) return
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(all))
  } catch (error) {
    logError(`Failed to save workflow`, error)
  }
}

export function loadWorkflow(userId: string | undefined, id: string): WorkflowDoc | undefined {
  const doc = readAll(userId)[id]
  // Drafts saved by an older builder carry an older node shape. Upgrade on the way out so a draft
  // written before a shape change still opens (and re-saves) as the canvas the user drew.
  return doc && { ...doc, nodes: upgradeStoredNodes(doc.nodes) }
}

export function saveWorkflow(userId: string | undefined, doc: WorkflowDoc): void {
  const all = readAll(userId)
  // The builder rewrites the whole doc on every save, so a first save is told apart from a re-save
  // here, where the previous copy is at hand.
  // A write that doesn't say (turning the workflow off) keeps the sync state the last save left.
  const previous = all[doc.id]
  all[doc.id] = {
    ...doc,
    createdAt: doc.createdAt ?? previous?.createdAt ?? Date.now(),
    unsynced: doc.unsynced ?? previous?.unsynced,
  }
  writeAll(userId, all)
}

/**
 * Re-key a local draft under the id the server stored it as, and record that id as its `remoteId`.
 *
 * The builder mints a local id before the first save, and the URL after a save has to address the
 * workflow the server actually holds. A local id there resolves to nothing for anyone who reopens
 * it (another browser, a shared link, cleared storage), and the blank canvas that used to render
 * invited a save that created a duplicate. The local copy is looked up by whatever the URL carries,
 * so the two ids have to agree.
 */
export function adoptServerId(userId: string | undefined, localId: string, serverId: string): void {
  const all = readAll(userId)
  const doc = all[localId]
  if (!doc) return
  if (localId !== serverId) delete all[localId]
  // Only called once the server accepted the save, so the copy is in sync from here.
  all[serverId] = { ...doc, id: serverId, remoteId: serverId, unsynced: false }
  writeAll(userId, all)
}

/**
 * Remove a saved workflow's local copies: the one asked for, and any other doc pointing at the same
 * server workflow.
 *
 * More than one can exist. A doc keyed by its original local id and a second keyed by the server id
 * both survive a delete-and-recreate, and the list only ever showed one of them, so deleting the
 * visible row used to leave the hidden twin behind — which then surfaced as a row for a workflow
 * the server no longer had.
 */
export function removeWorkflow(userId: string | undefined, id: string): void {
  const all = readAll(userId)
  const remoteId = all[id]?.remoteId
  delete all[id]
  if (remoteId) {
    for (const [key, doc] of Object.entries(all)) {
      if (doc.remoteId === remoteId) delete all[key]
    }
  }
  writeAll(userId, all)
}

/** Saved workflows, newest first. */
export function listWorkflows(userId: string | undefined): WorkflowDoc[] {
  return Object.values(readAll(userId)).sort((a, b) => b.updatedAt - a.updatedAt)
}
